// Port of TrailNavigation/HikingTimeEstimator.swift (HikingTimeEstimator and WalkingPerformanceModel).

import type { Confidence, EpochMs } from '../core/models';
import { HikingTime } from '../core/elevation';

function sanitize(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function finiteNumber(json: unknown, key: string): number {
  const value = json !== null && typeof json === 'object' ? (json as Record<string, unknown>)[key] : undefined;
  if (typeof value !== 'number' || !Number.isFinite(value)) throw new TypeError(`Expected a number for "${key}"`);
  return value;
}

/**
 * Walking-time estimate: the DIN 33466 baseline (`HikingTime`) scaled by a personal factor.
 *
 * Durations exclude breaks and are in seconds. A factor of 1.0 is the DIN baseline; 0.8 means the hiker needs 20 %
 * less time. The factor is always clamped to `factorRange` (non-finite values reset to 1.0), also when decoded, so a
 * corrupted preference can never produce absurd arrival times.
 */
export class HikingTimeEstimator {
  /** Allowed range for `personalFactor`. */
  static readonly factorRange = { lowerBound: 0.5, upperBound: 2.0 } as const;

  private factor: number;

  /** Creates an estimator; `personalFactor` is clamped to `factorRange`. */
  constructor(personalFactor = 1.0) {
    this.factor = HikingTimeEstimator.clamp(personalFactor);
  }

  /** 1.0 = DIN 33466 baseline; 0.8 = 20 % faster. Clamped 0.5…2.0 on every assignment. */
  get personalFactor(): number {
    return this.factor;
  }

  set personalFactor(value: number) {
    this.factor = HikingTimeEstimator.clamp(value);
  }

  /** Estimated walking duration in seconds for a distance (m) with ascent/descent (m). */
  duration(distance: number, ascent: number, descent: number): number {
    return HikingTime.estimate(sanitize(distance), sanitize(ascent), sanitize(descent)) * this.factor;
  }

  /** Estimated arrival time when starting at `start`. */
  arrival(start: EpochMs, distance: number, ascent: number, descent: number): EpochMs {
    return start + this.duration(distance, ascent, descent) * 1000;
  }

  static clamp(factor: number): number {
    if (!Number.isFinite(factor)) return 1.0;
    const { lowerBound, upperBound } = HikingTimeEstimator.factorRange;
    return Math.min(upperBound, Math.max(lowerBound, factor));
  }

  /** Codable shape `{"personalFactor": …}`. */
  toJSON(): { personalFactor: number } {
    return { personalFactor: this.factor };
  }

  /** Decodes and clamps the factor; throws when the field is missing or not a number (Swift `init(from:)`). */
  static fromJSON(json: unknown): HikingTimeEstimator {
    return new HikingTimeEstimator(finiteNumber(json, 'personalFactor'));
  }
}

/**
 * Learns the personal factor from completed walking (moving time vs DIN prediction), EWMA α=0.3, clamped.
 *
 * Each accepted sample contributes the ratio `movingTime / DIN(distance, ascent, descent)`. The ratio itself is
 * clamped to the factor range before averaging so a single outlier (a cable-car ride recorded as walking, a long
 * unrecorded break) moves the factor by at most 30 % of the gap. The average starts from the current factor (1.0 by
 * default), which keeps early estimates conservative; `confidence` tells the UI how much to trust it.
 */
export class WalkingPerformanceModel {
  /** EWMA smoothing weight of a new sample. */
  static readonly smoothing = 0.3;
  /** Samples shorter than this distance (m) are ignored. */
  static readonly minimumSampleDistance = 1000;
  /** Samples shorter than this moving time (s) are ignored. */
  static readonly minimumSampleDuration = 15 * 60;

  private currentFactor: number;
  private samples: number;

  /** Creates a model; the factor is clamped and the sample count floored at 0. */
  constructor(factor = 1.0, sampleCount = 0) {
    this.currentFactor = HikingTimeEstimator.clamp(factor);
    this.samples = Number.isFinite(sampleCount) ? Math.max(0, Math.trunc(sampleCount)) : 0;
  }

  /** Current personal factor (see `HikingTimeEstimator.personalFactor`). */
  get factor(): number {
    return this.currentFactor;
  }

  /** Number of samples that contributed to `factor`. */
  get sampleCount(): number {
    return this.samples;
  }

  /**
   * Records a completed stretch of walking (moving time in seconds). Ignores samples < 1 km or < 15 min, and samples
   * with non-finite or negative values.
   */
  record(movingTime: number, distance: number, ascent: number, descent: number): void {
    if (
      !Number.isFinite(movingTime) ||
      !Number.isFinite(distance) ||
      !Number.isFinite(ascent) ||
      !Number.isFinite(descent) ||
      ascent < 0 ||
      descent < 0 ||
      distance < WalkingPerformanceModel.minimumSampleDistance ||
      movingTime < WalkingPerformanceModel.minimumSampleDuration
    ) {
      return;
    }
    const predicted = HikingTime.estimate(distance, ascent, descent);
    if (!(predicted > 0)) return;
    const ratio = HikingTimeEstimator.clamp(movingTime / predicted);
    const alpha = WalkingPerformanceModel.smoothing;
    this.currentFactor = HikingTimeEstimator.clamp((1 - alpha) * this.currentFactor + alpha * ratio);
    this.samples += 1;
  }

  /** Estimator using the learned factor. */
  get estimator(): HikingTimeEstimator {
    return new HikingTimeEstimator(this.currentFactor);
  }

  /** <3 samples low, <10 medium, else high. */
  get confidence(): Confidence {
    if (this.samples < 3) return 'low';
    if (this.samples < 10) return 'medium';
    return 'high';
  }

  /** Codable shape `{"factor": …, "sampleCount": …}`. */
  toJSON(): { factor: number; sampleCount: number } {
    return { factor: this.currentFactor, sampleCount: this.samples };
  }

  /** Decodes, clamping the factor and flooring the sample count; throws on missing or non-numeric fields. */
  static fromJSON(json: unknown): WalkingPerformanceModel {
    const sampleCount = finiteNumber(json, 'sampleCount');
    if (!Number.isInteger(sampleCount)) throw new TypeError('Expected an integer for "sampleCount"');
    return new WalkingPerformanceModel(finiteNumber(json, 'factor'), sampleCount);
  }
}
